/**
 * Task #1290 — Hook gate wizard scelta embedder.
 *
 * Restituisce { needsWizard, status, refetch } basato su GET /tenant/embedder-status.
 * `needsWizard = true` quando il tenant è autenticato (non superadmin) E
 * `embedder_provider IS NULL` OPPURE `locked_at IS NULL` (cioè la scelta non
 * è ancora stata committata in modo irreversibile).
 *
 * Il wizard non parte mai per:
 *   - utenti non autenticati
 *   - role super_admin (non opera dentro un tenant)
 *   - status già lockato (locked_at != null)
 */

import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import apiClient from '@/lib/api';

export interface EmbedderStatus {
  configured: boolean;
  current: 'openai' | 'gemini' | null;
  model: string | null;
  has_api_keys: boolean;
  locked_at: string | null;
  is_locked: boolean;
  image_chunks_enabled: boolean;
  gemini_chat_model: string | null;
  /** Task #2491 — leva chat-model per-tenant speculare per embedder=openai. */
  openai_chat_model: string | null;
  /**
   * Task #1409 — opt-in indicizzazione immagini inline estratte dallo scrape
   * URL come chunk multimodali Gemini. Default false.
   */
  wiki_scrape_index_media: boolean;
}

export const EMBEDDER_STATUS_QUERY_KEY = ['tenant', 'embedder-status'] as const;

export function useEmbedderGate() {
  const { user, isAuthenticated } = useAuth();
  const isSuperAdmin = user?.role === 'super_admin';
  const enabled = isAuthenticated && !isSuperAdmin;

  const { data, isLoading, refetch } = useQuery<EmbedderStatus | null>({
    queryKey: EMBEDDER_STATUS_QUERY_KEY,
    queryFn: async () => {
      try {
        const resp = await apiClient.get<EmbedderStatus>('/tenant/embedder-status');
        if (!resp?.success || !resp.data) return null;
        return resp.data;
      } catch {
        return null;
      }
    },
    enabled,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
    retry: false,
  });

  // Dismiss temporaneo per la sessione: l'utente può chiudere il wizard
  // se non vuole scegliere subito; verrà rimostrato al prossimo login.
  const dismissedThisSession =
    typeof window !== 'undefined' &&
    window.sessionStorage.getItem('embedder-wizard-dismissed') === '1';

  const needsWizard =
    enabled && !isLoading && !!data && data.is_locked === false && !dismissedThisSession;

  return { needsWizard, status: data ?? null, isLoading, refetch };
}

export function dismissEmbedderWizardForSession() {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem('embedder-wizard-dismissed', '1');
  }
}
